import { isAfter, isBefore, parseISO } from 'date-fns';
import { isEmpty, orderBy } from 'lodash';
import { node } from 'prop-types';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { PAGE_TYPES } from 'constants';
import { useCurrentRoute, useFetch, useSafeState } from 'hooks';
import { appService } from 'services';
import { setFooterBanner } from 'store';

import 'assets/styles/components/pages/home-page.scss';

const HomePageContext = createContext();
export const useHomePageContext = () => useContext(HomePageContext);

const NEWS_CARDS_MAX_COUNT = 4;

const HomePageContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [promoteTitle, setPromoteTitle] = useSafeState('');

  const { call: homePageCall, data: homePageData, loading: homePageLoading } = useFetch({ filterUserCompany: true });
  const { pageType } = useCurrentRoute();

  const homePage = useMemo(() => homePageData?.data?.[0], [homePageData]);

  const featuredProducts = useMemo(() => {
    let products = [];

    if (!isEmpty(homePage?.slideOffer)) {
      products = homePage.slideOffer.filter((offer) => {
        if (!offer.startDate && !offer.endDate) {
          return true;
        } else {
          return isAfter(new Date(), parseISO(offer?.startDate)) && isBefore(new Date(), parseISO(offer?.endDate));
        }
      });
    }

    return orderBy(products, ['order']);
  }, [homePage, location.pathname]);

  const newsCards = useMemo(() => {
    if (homePage?.newsCard?.length < NEWS_CARDS_MAX_COUNT) {
      return [];
    } else {
      return homePage?.newsCard?.slice(0, NEWS_CARDS_MAX_COUNT);
    }
  }, [homePage, location.pathname]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    const footerBanner = homePage?.helpBanner;

    if (!isEmpty(footerBanner)) {
      dispatch(setFooterBanner(footerBanner));
    }
  }, [homePage]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getHomePage = async () => {
    const { data } =
      (await homePageCall({
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: '/api/homepages',
        query: {
          populate: 'deep,4',
          filters: {
            page_type: {
              name: { $eq: pageType }
            }
          }
        }
      })) || {};

    if (pageType !== PAGE_TYPES.giftcards) {
      const title = data?.body?.data?.[0]?.featuredProductsTitle || t(`home.${pageType}.promoteTitle`);
      setPromoteTitle(title);
    }
  };

  return (
    <HomePageContext.Provider
      value={{
        getHomePage,
        homePage,
        homePageLoading,
        pageType,
        featuredProducts,
        newsCards,
        promoteTitle,
        setPromoteTitle
      }}
    >
      {children}
    </HomePageContext.Provider>
  );
};

HomePageContextProvider.propTypes = {
  children: node.isRequired
};

export default HomePageContextProvider;
